<script setup lang="ts">
import { ref, onMounted } from 'vue'

const currentYear = new Date().getFullYear()
const name = ref('')
const email = ref('')
const status = ref<'idle' | 'success' | 'error'>('idle')

const scrollToSection = (href: string) => {
  const element = document.querySelector(href)
  if (element) {
    const headerOffset = 80
    const elementPosition = element.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
  }
}

const submitContact = async (e: Event) => {
  e.preventDefault()
  
  const formData = new FormData()
  formData.append('form-name', 'contact')
  formData.append('name', name.value)
  formData.append('email', email.value)
  
  try {
    await fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as any).toString()
    })
    
    status.value = 'success'
    name.value = ''
    email.value = ''
    setTimeout(() => {
      status.value = 'idle'
    }, 3000)
  } catch (error) {
    console.error('Error:', error)
    status.value = 'error'
  }
}
</script>

<template>
  <footer class="footer">
    <div class="container">
      <div class="footer-content">
        <div class="footer-section">
          <h3 class="footer-title">Krilom</h3>
          <p>Revolutionizing omega-3 supplementation through sustainable Antarctic krill oil products.</p>
        </div>
        
        <div class="footer-section">
          <h4>Quick Links</h4>
          <ul>
            <li><a href="#products" @click.prevent="scrollToSection('#products')">Products</a></li>
            <li><a href="#science" @click.prevent="scrollToSection('#science')">Science</a></li>
            <li><a href="#contact" @click.prevent="scrollToSection('#contact')">Contact</a></li>
          </ul>
        </div>
        
        <div class="footer-section">
          <h4>Contact</h4>
          <ul>
            <li>
              <form 
                name="contact" 
                method="POST" 
                data-netlify="true"
                @submit.prevent="submitContact"
              >
                <input type="hidden" name="form-name" value="contact" />
                <input type="hidden" name="bot-field" />
                <p>
                  <label>Name <input type="text" name="name" v-model="name" required /></label>
                </p>
                <p>
                  <label>Email <input type="email" name="email" v-model="email" required /></label>
                </p>
                <p>
                  <button type="submit">Send</button>
                </p>
                <p v-if="status === 'success'" class="success-message">
                  Message sent successfully! 🎉
                </p>
              </form>
            </li>
            <li>Email: info@krilom.com</li>
            <li>Phone: +1 (555) 123-4567</li>
          </ul>
        </div>
      </div>
      
      <div class="footer-bottom">
        <p>&copy; {{ currentYear }} Krilom. All rights reserved.</p>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.footer {
  background: linear-gradient(to bottom, rgba(9, 12, 23, 0.3), rgba(26, 36, 66, 0.6));
  border-top: 1px solid rgba(227, 20, 71, 0.1);
  padding: 4rem 2rem 2rem;
  position: relative;
  z-index: 1;
  width: 100%;
  box-sizing: border-box;
  color: #E2E2E2;
}

.footer-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  margin-bottom: 2rem;
  width: 100%;
  position: relative;
  z-index: 2;
  text-align: left;
}

.footer-section h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #e31447;
  text-shadow: 0 0 10px rgba(227, 20, 71, 0.3);
}

.footer-section h4 {
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: #E2E2E2;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
}

.footer-section p {
  color: #4a9eca;
  line-height: 1.6;
  margin: 0;
  opacity: 0.8;
}

.footer-section ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-section ul li {
  margin-bottom: 0.5rem;
  color: #4a9eca;
  opacity: 0.8;
}

.footer-section ul li a {
  color: #4a9eca;
  text-decoration: none;
  transition: color 0.3s ease;
  display: inline-block;
  opacity: 0.8;
}

.footer-section ul li a:hover {
  color: #e31447;
  text-shadow: 0 0 10px rgba(227, 20, 71, 0.3);
  opacity: 1;
}

.footer-bottom {
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(227, 20, 71, 0.1);
  color: #4a9eca;
  font-size: 0.9rem;
  margin-top: 2rem;
  position: relative;
  z-index: 2;
  opacity: 0.8;
}
</style>